/**
 * API Configuration - Centralized for Entire Application
 * All features (Auth, Feed, Profile, Settings, etc.) use this
 * 
 * Location: ${TOP-SRC}/src/components/config/apiConfig.js
 */

// ✅ Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://34.131.127.231:8080/api/v1";

// ✅ Enable mock mode for testing (set to false in production)
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true' || false;

// ✅ API ENDPOINTS - All application endpoints in one place
export const API_ENDPOINTS = {
  // ========================================
  // AUTH ENDPOINTS
  // ========================================
  AUTH: {
    SIGNUP: `${API_BASE_URL}/auth/signup`,
    LOGIN: `${API_BASE_URL}/auth/login`,
    LOGOUT: `${API_BASE_URL}/auth/logout`,
    REFRESH_TOKEN: `${API_BASE_URL}/auth/refresh`,
    FORGOT_PASSWORD: `${API_BASE_URL}/auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}/auth/reset-password`,
    VERIFY_EMAIL: `${API_BASE_URL}/auth/verify-email`,
  },

  // ========================================
  // OTP ENDPOINTS ✅ NEW
  // ========================================
  OTP: {
    GENERATE: `${API_BASE_URL}/user/otp/generate`,
    VALIDATE: `${API_BASE_URL}/user/otp/isvalid`,
    RESEND: `${API_BASE_URL}/user/otp/resend`,
  },

  // ========================================
  // USER ENDPOINTS
  // ========================================
  USER: {
    GET_PROFILE: (userId) => `${API_BASE_URL}/user/${userId}`,
    UPDATE_PROFILE: (userId) => `${API_BASE_URL}/user/${userId}`,
    DELETE_ACCOUNT: (userId) => `${API_BASE_URL}/user/${userId}`,
    GET_SETTINGS: (userId) => `${API_BASE_URL}/user/${userId}/settings`,
    UPDATE_SETTINGS: (userId) => `${API_BASE_URL}/user/${userId}/settings`,
    UPLOAD_AVATAR: (userId) => `${API_BASE_URL}/user/${userId}/avatar`,
  },

  // ========================================
  // FEED ENDPOINTS
  // ========================================
  FEED: {
    GET_FEED: `${API_BASE_URL}/feed`,
    GET_POST: (postId) => `${API_BASE_URL}/feed/post/${postId}`,
    CREATE_POST: `${API_BASE_URL}/feed/post`,
    UPDATE_POST: (postId) => `${API_BASE_URL}/feed/post/${postId}`,
    DELETE_POST: (postId) => `${API_BASE_URL}/feed/post/${postId}`,
    LIKE_POST: (postId) => `${API_BASE_URL}/feed/post/${postId}/like`,
    COMMENT_POST: (postId) => `${API_BASE_URL}/feed/post/${postId}/comment`,
  },

  // ========================================
  // CONTENT/VIDEO ENDPOINTS
  // ========================================
  CONTENT: {
    GET_VIDEOS: `${API_BASE_URL}/content/videos`,
    GET_VIDEO: (videoId) => `${API_BASE_URL}/content/video/${videoId}`,
    UPLOAD_VIDEO: `${API_BASE_URL}/content/upload`,
    GET_BREAKPOINTS: (videoId) => `${API_BASE_URL}/content/video/${videoId}/breakpoints`,
    CREATE_BREAKPOINT: `${API_BASE_URL}/content/breakpoint`,
    SUBMIT_TO_AI: `${API_BASE_URL}/content/generate-narrative`,
  },

  // ========================================
  // NOTIFICATION ENDPOINTS
  // ========================================
  NOTIFICATIONS: {
    GET_ALL: (userId) => `${API_BASE_URL}/notifications/${userId}`,
    MARK_READ: (notificationId) => `${API_BASE_URL}/notifications/${notificationId}/read`,
    DELETE: (notificationId) => `${API_BASE_URL}/notifications/${notificationId}`,
  },

  // ========================================
  // FEATURE/SUBSCRIPTION ENDPOINTS
  // ========================================
  FEATURES: {
    GET_ALL: `${API_BASE_URL}/features`,
    SUBSCRIBE: `${API_BASE_URL}/features/subscribe`,
    UNSUBSCRIBE: (featureId) => `${API_BASE_URL}/features/${featureId}/unsubscribe`,
    GET_SUBSCRIPTIONS: (userId) => `${API_BASE_URL}/features/subscriptions/${userId}`,
  },

  // ========================================
  // ANALYTICS ENDPOINTS
  // ========================================
  ANALYTICS: {
    GET_STATS: (userId) => `${API_BASE_URL}/analytics/${userId}`,
    TRACK_EVENT: `${API_BASE_URL}/analytics/track`,
  },

  // ========================================
  // SEARCH ENDPOINTS
  // ========================================
  SEARCH: {
    GLOBAL: `${API_BASE_URL}/search`,
    USERS: `${API_BASE_URL}/search/users`,
    POSTS: `${API_BASE_URL}/search/posts`,
    VIDEOS: `${API_BASE_URL}/search/videos`,
  },
};

// Export base URL and mock mode flag
export { API_BASE_URL, USE_MOCK_DATA };

// ========================================
// MOCK DATA FOR TESTING ✅ NEW
// ========================================
const MOCK_RESPONSES = {
  OTP_GENERATE: {
    success: true,
    message: 'OTP sent successfully',
    otp: '123456', // For testing only - don't expose in production!
  },
  OTP_VALIDATE: {
    success: true,
    message: 'OTP verified successfully',
    deviceId: `DEVICE_${Date.now()}`,
  },
  SIGNUP: {
    status: 'success',
    message: 'User registered successfully',
    userId: `USER_${Date.now()}`,
    securityToken: `TOKEN_${Date.now()}_MOCK`,
  },
  // ✅ ADD THIS: Login mock response
  LOGIN: {
    userUuid: `UUID_${Date.now()}`,
    status: 'success',
    sessionId: `SESSION_${Date.now()}_MOCK`,
    errorCode: null,
    message: 'Login successful',
    mfaRequired: false,
    userType: 'general_user',
    accountStatus: 'active',
    lastLoginAt: new Date().toISOString(),
    loginAttempt: 1,
  },
};


// ========================================
// CENTRALIZED API CALL FUNCTION
// ========================================

/**
 * Universal API call wrapper with error handling, timeout, and authentication
 * Used by ALL services across the application
 * 
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @param {number} timeout - Request timeout in milliseconds
 * @param {boolean} useMock - Force mock response (for testing)
 * @returns {Promise<object>} - Standardized response object
 */
export const apiCall = async (url, options = {}, timeout = 30000, useMock = false) => {
  // ✅ Check if mock mode is enabled
  if (USE_MOCK_DATA || useMock) {
    console.log('🎭 MOCK MODE: Returning mock data for:', url);
    return getMockResponse(url, options);
  }

  console.log('🌐 API Call:', url);
  console.log('📋 Options:', options);

  // ✅ Add authentication token if available
  const token = localStorage.getItem('securityToken');
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };

  // ✅ Setup timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);
    console.log('📡 Response Status:', response.status);

    // ✅ Handle different response types
    let data;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      console.warn('⚠️ Non-JSON response:', text);
      data = { message: text };
    }

    console.log('📦 Response Data:', data);

    // ✅ Handle success
    if (response.ok) {
      return { 
        success: true, 
        data,
        status: response.status 
      };
    }

    // ✅ Handle authentication errors
    if (response.status === 401) {
      console.warn('🔒 Authentication failed - redirecting to login');
      localStorage.removeItem('securityToken');
      localStorage.removeItem('userId');
      window.location.href = '/login';
      return {
        success: false,
        message: 'Session expired. Please login again.',
        status: 401,
      };
    }

    // ✅ Handle other errors
    return {
      success: false,
      message: data.message || data.error || 'API request failed',
      status: response.status,
      data,
    };

  } catch (error) {
    clearTimeout(timeoutId);
    
    // ✅ Handle timeout
    if (error.name === 'AbortError') {
      console.error('⏱️ Request Timeout');
      return {
        success: false,
        message: 'Request timeout. Please try again.',
        status: 408,
      };
    }

    // ✅ Handle network errors
    console.error('❌ API Call Error:', error);
    return {
      success: false,
      message: error.message || 'Network error. Please check your connection.',
      error: error.toString(),
      status: 0,
    };
  }
};

// ========================================
// MOCK RESPONSE HANDLER ✅ NEW
// ========================================

/**
 * Get mock response based on endpoint
 */
// Update getMockResponse function in apiConfig.js
const getMockResponse = async (url, options) => {
  await new Promise(resolve => setTimeout(resolve, 500));

  let requestBody = {};
  if (options.body) {
    try {
      requestBody = JSON.parse(options.body);
    } catch (e) {
      console.warn('Could not parse request body');
    }
  }

  // OTP Generate endpoint
  if (url.includes('/otp/generate')) {
    console.log('🎭 Mock: OTP Generate for mobile:', requestBody.mobile);
    return {
      success: true,
      data: {
        ...MOCK_RESPONSES.OTP_GENERATE,
        mobile: requestBody.mobile,
      }
    };
  }

  // OTP Validate endpoint
  if (url.includes('/otp/isvalid')) {
    console.log('🎭 Mock: OTP Validate -', requestBody.otp);
    const isValidOTP = requestBody.otp && requestBody.otp.length === 6;
    
    if (isValidOTP) {
      return {
        success: true,
        data: {
          ...MOCK_RESPONSES.OTP_VALIDATE,
          mobile: requestBody.mobile,
        }
      };
    } else {
      return {
        success: false,
        data: {
          success: false,
          message: 'Invalid OTP format',
        }
      };
    }
  }

  // ✅ ADD THIS: Signup endpoint
  if (url.includes('/auth/signup')) {
    console.log('🎭 Mock: Signup for user:', requestBody.userId);
    return {
      success: true,
      data: {
        ...MOCK_RESPONSES.SIGNUP,
        userId: requestBody.userId || `USER_${Date.now()}`,
        name: requestBody.name,
        email: requestBody.email,
      }
    };
  }

  // ✅ ADD THIS: Login endpoint
  if (url.includes('/auth/login')) {
    console.log('🎭 Mock: Login for:', requestBody.userId || requestBody.email || requestBody.mobileNo);
    
    // Mock password validation (accept "password123" or any 8+ char password in mock mode)
    const isValidPassword = requestBody.password && requestBody.password.length >= 8;
    
    if (isValidPassword) {
      return {
        success: true,
        data: {
          ...MOCK_RESPONSES.LOGIN,
          userUuid: requestBody.userId || requestBody.email || requestBody.mobileNo,
        }
      };
    } else {
      return {
        success: false,
        data: {
          status: 'failure',
          message: 'Invalid credentials',
          errorCode: 401,
        }
      };
    }
  }

  // Default mock response
  console.log('🎭 Mock: Default response for:', url);
  return {
    success: true,
    data: { message: 'Mock response' }
  };
};


// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Build custom API URL
 */
export const buildApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint}`;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('securityToken');
};

/**
 * Get current user ID
 */
export const getCurrentUserId = () => {
  return localStorage.getItem('userId');
};

/**
 * Clear authentication data
 */
export const clearAuth = () => {
  localStorage.removeItem('securityToken');
  localStorage.removeItem('userId');
};

/**
 * Enable/Disable Mock Mode (for testing)
 */
export const setMockMode = (enabled) => {
  if (enabled) {
    console.log('🎭 Mock Mode ENABLED');
  } else {
    console.log('🌐 Mock Mode DISABLED - Using real API');
  }
  // Note: This only affects runtime. To persist, update .env file
};

// ========================================
// CONFIGURATION LOGGING
// ========================================
console.log('🔗 API Configuration Loaded');
console.log('📍 Base URL:', API_BASE_URL);
console.log('🌍 Environment:', process.env.NODE_ENV);
console.log('🎭 Mock Mode:', USE_MOCK_DATA ? 'ENABLED' : 'DISABLED');
console.log('🔐 Authentication Token Present:', isAuthenticated());
